import { useCallback, useEffect, useState } from 'react';
import { loadEmployeeDirectory, type DirectoryEmployee } from './employeeDirectory';

export type DirectoryState =
  | { kind: 'loading' }
  | { kind: 'ready'; employees: DirectoryEmployee[] }
  | { kind: 'error' };

/**
 * The company's employees from the Employees API (names are never read from a benefit or deduction service). A failed read leaves the screens working with
 * ids shown as "Unknown employee": the benefit and deduction data itself does not depend on it.
 */
export function useEmployeeDirectory(): { state: DirectoryState; nameOf: (id: string) => string; reload: () => void } {
  const [state, setState] = useState<DirectoryState>({ kind: 'loading' });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    loadEmployeeDirectory()
      .then((employees) => { if (!cancelled) setState({ kind: 'ready', employees }); })
      .catch(() => { if (!cancelled) setState({ kind: 'error' }); });
    return () => { cancelled = true; };
  }, [nonce]);

  const nameOf = useCallback((id: string) => {
    if (state.kind !== 'ready') return 'Employee';
    return state.employees.find((e) => e.id === id)?.name ?? 'Unknown employee';
  }, [state]);

  const reload = useCallback(() => { setState({ kind: 'loading' }); setNonce((n) => n + 1); }, []);
  return { state, nameOf, reload };
}
